import { ReactNode } from 'react';

import { useLanguage } from '~/contexts/lang/languageContext';
import { useTenantSlug } from '~/features/tenant/helpers';
import QueryResolver from '~/services/api/query-resolver';
import { TenantService } from '~/services/api/tenant/endpoints';
import { useIntakeChannel } from '~/slices/intakeChannelSlice';

export function CheckMetricWrapper({
  metricCategory,
  metricName,
  children,
}: Readonly<{
  metricCategory: string;
  metricName: string;
  children: ReactNode;
}>) {
  const slug = useTenantSlug();
  const { selectedLanguage } = useLanguage();
  const selectedChannel = useIntakeChannel().intakeChannelData.selectedChannel;

  const query = TenantService.endpoints.intakeChannelProgramDetails.useQuery(
    {
      slug: slug,
      intakeChannelId: selectedChannel ?? -1,
      params: {
        language_id: selectedLanguage.id,
      },
    },
    { skip: !selectedChannel || !slug },
  );

  if (!query.data?.workflow_configuration) {
    // Temporary backward compatibility for old metrics
    return <QueryResolver query={query}>{() => <>{children}</>}</QueryResolver>;
  }

  const isMetricEnabled = query.data?.workflow_configuration.dashboard_metrics
    ?.find((metric) => metric.category === metricCategory)
    ?.metrics.find((metric) => metric.name === metricName);

  if (!isMetricEnabled) {
    return null;
  }

  return <QueryResolver query={query}>{() => <>{children}</>}</QueryResolver>;
}

// check if the metric is enabled
export function useAvailableMetrics({ category }: { category: string }) {
  const slug = useTenantSlug();
  const { selectedLanguage } = useLanguage();
  const selectedChannel = useIntakeChannel().intakeChannelData.selectedChannel;

  const query = TenantService.endpoints.intakeChannelProgramDetails.useQuery(
    {
      slug: slug,
      intakeChannelId: selectedChannel ?? -1,
      params: {
        language_id: selectedLanguage.id,
      },
    },
    { skip: !selectedChannel || !slug },
  );

  const isMetricEnabled =
    query.data?.workflow_configuration?.dashboard_metrics?.find(
      (metric) => metric.category === category,
    );

  return isMetricEnabled?.metrics ?? [];
}
